import type { FieldPath, FieldPathValue, FieldValues } from 'react-hook-form';
import {
  Checkbox,
  Input,
  MultiSelect,
  NumberInput,
  PasswordInput,
  Select,
  Switch,
  Textarea,
  TextInput,
} from '@mantine/core';
import type {
  CheckboxProps,
  InputWrapperProps,
  MultiSelectProps,
  NumberInputProps,
  PasswordInputProps,
  SelectProps,
  SwitchProps,
  TextareaProps,
  TextInputProps,
} from '@mantine/core';
import { DatePickerInput, DateTimePicker, TimePicker, YearPickerInput } from '@mantine/dates';
import type {
  DatePickerInputProps,
  DateTimePickerProps,
  TimePickerProps,
  YearPickerInputProps,
} from '@mantine/dates';
import { FormField } from './form';
import useFormField from './use-form-field';

/**
 * Component props mapping for different input types
 */
export type ComponentPropsMap = {
  password: PasswordInputProps;
  input: TextInputProps;
  number: NumberInputProps;
  textarea: TextareaProps;
  select: SelectProps & { options?: Array<{ value: string; label: string }> | Array<never> };
  multiselect: MultiSelectProps;
  checkbox: CheckboxProps;
  switch: SwitchProps;
  date: DatePickerInputProps;
  dateTime: DateTimePickerProps;
  time: TimePickerProps;
  year: YearPickerInputProps;
};

/**
 * Dynamic Props resolver based on input type
 */
export type DynamicProps<T extends keyof ComponentPropsMap> = ComponentPropsMap[T];

/**
 * Props for the FormInputWrapper component
 */
export type FormInputWrapperProps<
  T extends keyof ComponentPropsMap,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  label?: string;
  name: TName;
  type: T;
  inputProps: DynamicProps<T>;
  defaultValue?: FieldPathValue<TFieldValues, TName>;
};

const defaultProps: InputWrapperProps = {
  inputWrapperOrder: ['label', 'input', 'error', 'description'],
};

/**
 * Creates a form field configuration dynamically based on type
 */
export function createFormField<T extends keyof ComponentPropsMap>(
  type: T,
  inputProps: DynamicProps<T>
) {
  return { inputType: type, inputProps };
}

/**
 * Maps form field types to respective Mantine components
 */
const mapComponent = {
  input: (props: TextInputProps) => (
    <TextInput
      {...props}
      value={props.value ?? ''}
      onBlur={props.onBlur}
      onChange={(val) => {
        if (props?.onChange) {
          props?.onChange(val ?? '');
        }
      }}
    />
  ),
  number: (props: NumberInputProps) => <NumberInput {...props} />,
  password: (props: PasswordInputProps) => <PasswordInput {...props} />,
  textarea: (props: TextareaProps) => <Textarea {...props} />,
  select: (props: SelectProps) => (
    <Select searchable {...props} nothingFoundMessage="Data tidak ditemukan" />
  ),
  multiselect: (props: MultiSelectProps) => (
    <MultiSelect clearable {...props} value={props.value ?? []} />
  ),
  checkbox: (props: CheckboxProps) => <Checkbox {...props} />,
  switch: (props: SwitchProps) => <Switch {...props} checked={!!props.value} />,
  date: (props: DatePickerInputProps) => {
    if (props.type === 'range') {
      return <DatePickerInput {...props} type="range" value={props?.value} />;
    }
    return <DatePickerInput {...props} value={props.value ? new Date(props.value) : null} />;
  },
  year: (props: YearPickerInputProps) => {
    return (
      <YearPickerInput
        {...props}
        value={props.value}
        onChange={(value) => {
          if (typeof value === 'string' || value === null) {
            props?.onChange?.(value);
          }
        }}
      />
    );
  },
  dateTime: (props: DateTimePickerProps) => <DateTimePicker clearable {...props} />,
  time: (props: TimePickerProps) => <TimePicker {...props} />,
};

/**
 * Wrapper component for form inputs using Mantine and React Hook Form
 */
export const FormInputWrapper = <
  T extends keyof ComponentPropsMap | 'input',
  TFieldValues extends FieldValues,
>({
  name,
  type,
  inputProps,
  label,
  defaultValue,
}: FormInputWrapperProps<T, TFieldValues>) => {
  const { id } = useFormField();
  const fieldConfig = createFormField(type, inputProps);
  return (
    <FormField
      name={name}
      defaultValue={defaultValue}
      render={({ field, fieldState }) => (
        <Input.Wrapper id={id} error={fieldState.error?.message} {...defaultProps} label={label}>
          {mapComponent[type]({
            ...field,
            ...(fieldConfig.inputProps as typeof fieldConfig),
          })}
        </Input.Wrapper>
      )}
    />
  );
};
